import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { clearInsightsCache } from "@/lib/useInsights";

export const TopicDescriptionsEditor = () => {
  const [qs, setQs] = useState(null);
  const [desc, setDesc] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/public/insights").then((r) => {
      setQs([...(r.data.questions || [])].sort((a, b) => a.label.localeCompare(b.label)));
      setDesc(r.data.topic_descriptions || {});
    }).catch(() => {});
  }, []);

  if (!qs) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/topic-descriptions", { descriptions: desc });
      setDesc(data.descriptions);
      clearInsightsCache();
      toast.success("Topic descriptions saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const filled = qs.filter((q) => (desc[q.key] || "").trim()).length;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="topic-descriptions-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Topic descriptions</h2>
          <p className="mt-1 text-sm text-slate-500">
            One paragraph per topic. Shown on reports for each person's two lowest-rated topics in every area. {filled} of {qs.length} written.
          </p>
        </div>
        <Button data-testid="save-topic-descriptions-button" onClick={save} disabled={busy} className="rounded-full bg-[#1F4266] hover:bg-[#122A43]">
          <Save className="mr-2 h-4 w-4" /> Save descriptions
        </Button>
      </div>
      <div className="mt-5 max-h-[640px] space-y-4 overflow-y-auto pr-2">
        {qs.map((q) => (
          <div key={q.key}>
            <div className="mb-1 text-sm font-semibold text-slate-800">{q.label}</div>
            <Textarea
              data-testid={`topic-description-${q.key}`}
              rows={3}
              placeholder={`Describe ${q.label}…`}
              value={desc[q.key] || ""}
              onChange={(e) => setDesc({ ...desc, [q.key]: e.target.value })}
            />
          </div>
        ))}
      </div>
    </section>
  );
};
